import type { SqlRunner } from '../../../../lib/db.js';
import { buildPublicToConnectedSql } from './columns.js';
import { AUDIT_ACTOR_FIELDS, withAuditActors } from './history.js';
import type { RawAuditVersionRow } from './transformers.js';

/** The audit here is aliased `a`; `$1` is the viewer's company. */
const PUBLIC_TO_CONNECTED = buildPublicToConnectedSql('a', '$1');

export interface ListAuditVersionsDbParams {
  viewerCompanyId: string;
  /** The reference audit — versions share its (audit_type_id, company_id). */
  auditId: string;
  page: number;
  count: number;
}

export interface ListAuditVersionsDbResult {
  rows: RawAuditVersionRow[];
  totalCount: number;
  pageSize: number;
}

/**
 * Version history of one audit: every other audit with the reference audit's
 * (audit_type_id, company_id) that is shared with the viewer (ACTIVE or
 * ARCHIVED share). Verbatim transcription of the legacy
 * `audits-service/db/queries/audits.js#versions`, with three deliberate
 * changes:
 *
 * 1. `bucket_name` is selected for both files — the service signs preview
 *    URLs from the joined columns instead of the legacy per-row double
 *    `filesDB.getById` re-fetch (list precedent, batch 2).
 * 2. A deterministic ORDER BY — the legacy query had none, so pagination
 *    could duplicate/skip rows across pages. The ordering is the version-rank
 *    ordering the list uses to pick the "active" version
 *    (`buildVersionRankJoin` in `columns.ts`), with `id` as total tiebreaker.
 * 3. LIMIT/OFFSET are appended as binds (replacing the deprecated
 *    `db.readPagination`).
 * 4. A version also qualifies when it is public and the audited company is
 *    connected to the viewer (`buildPublicToConnectedSql`) — the same rule the
 *    list and the by-id read apply, so a history entry cannot be unreachable
 *    behind an audit the viewer is allowed to open.
 */
export const listAuditVersionsDb = async (
  db: SqlRunner,
  params: ListAuditVersionsDbParams,
): Promise<ListAuditVersionsDbResult> => {
  const { auditId, count, page, viewerCompanyId } = params;

  const pageRows = await db.runSql<RawAuditVersionRow>(
    `SELECT
      a.id,
      a.company_id,
      COALESCE(
        company_profiles.data->'display_name'->>'value',
        company_profiles.data->'official_name'->>'value',
        a.company_name
      ) as company_name,
      company_profiles.data->'city'->>'value' AS city,
      a.name,
      a.audit_type_id,
      audit_types.name as audit_type_name,
      a.type,
      a.audited_by,
      a.audit_date,
      a.reference_number,
      a.facility_score,
      a.next_planned_audit_date,
      a.audit_report_file_id,
      audit_report_file.url AS audit_report_file_url,
      audit_report_file.bucket_name AS audit_report_file_bucket_name,
      audit_report_file.file_type AS audit_report_file_type,
      audit_report_file.content_type AS audit_report_file_content_type,
      a.corrective_action_plan_file_id,
      corrective_action_plan_file.url AS corrective_action_plan_file_url,
      corrective_action_plan_file.bucket_name AS corrective_action_plan_file_bucket_name,
      corrective_action_plan_file.file_type AS corrective_action_plan_file_file_type,
      corrective_action_plan_file.content_type AS corrective_action_plan_file_content_type,
      a.uploader_company_id,
      a.uploader_company_name,
      a.created_at,
      a.created_by_company_id,
      a.updated_at,
      COALESCE(
        asset_access_control.rights,
        CASE WHEN ${PUBLIC_TO_CONNECTED} THEN '["READ"]'::jsonb END
      ) as rights,
      a.is_public,
      COUNT(a.id) OVER() as count
    FROM audits_service_audits a
    LEFT JOIN audits_service_audit_types audit_types
      ON audit_types.id = a.audit_type_id
    LEFT JOIN companies_service_company_profiles company_profiles
      ON company_profiles.for_company_id = a.company_id
      AND company_profiles.by_company_id = $1
      AND company_profiles.on_behalf_of_company_id = $1
      AND company_profiles.profile_type = $2
    LEFT JOIN files audit_report_file
      ON a.audit_report_file_id = audit_report_file.id
    LEFT JOIN files corrective_action_plan_file
      ON a.corrective_action_plan_file_id = corrective_action_plan_file.id
    LEFT JOIN companies_service_asset_access_control asset_access_control
      ON asset_access_control.asset_id = a.id
      AND asset_access_control.asset_type = $4
      AND asset_access_control.shared_with_company_id = $1
      AND asset_access_control.status in ($5, $6)
    JOIN audits_service_audits ref_audit ON ref_audit.id = $3
    WHERE (
        asset_access_control.asset_id IS NOT NULL
        OR ${PUBLIC_TO_CONNECTED}
      )
      AND a.id <> $3
      AND a.audit_type_id = ref_audit.audit_type_id
      AND a.company_id = ref_audit.company_id
    ORDER BY a.next_planned_audit_date DESC NULLS LAST,
      a.audit_date DESC NULLS LAST,
      a.created_at DESC,
      a.id DESC
    LIMIT ($7)::bigint OFFSET ($8)::bigint`,
    [
      viewerCompanyId,
      'ABOUT',
      auditId,
      'AUDITS',
      'ACTIVE',
      'ARCHIVED',
      count,
      page * count,
    ],
  );
  // The actors live in entity_history; read them for this page only, since
  // the query above counts every version.
  const rows = await withAuditActors(db, pageRows, AUDIT_ACTOR_FIELDS);

  // COUNT(*) OVER() is bigint — number or string depending on the driver's
  // type parser. Extracted from the RAW row, before any censoring, fixing the
  // legacy bug where a censored first row collapsed `meta.totalCount` to 0.
  const totalCount =
    rows.length > 0 && rows[0].count != null ? Number(rows[0].count) : 0;

  return { rows, totalCount, pageSize: count };
};
